import React, { useState, useEffect } from 'react';
import UIModal from '@/components/shared/UIModal';
import UISelect from '@/components/shared/UISelect';
import UIButton from '@/components/shared/UIButton';
import useGetDictionaries from '@/hooks/useGetDictionaries';
import './AddPaymentModal.scss';

interface AddPaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit?: (paymentData: AddPaymentData) => void;
}

interface AddPaymentData {
  tenantId: string;
  paymentType: string;
  amount: number;
  description: string;
}

interface PaymentDictionaries {
  properties: Array<{
    id: number;
    name: string;
  }>;
  tenants: Array<{
    id: number;
    name: string;
  }>;
  periods: Array<{
    value: string;
    label: string;
  }>;
  paymentTypes: Array<{
    id: string;
    name: string;
  }>;
}

const AddPaymentModal: React.FC<AddPaymentModalProps> = ({
                                                           isOpen,
                                                           onClose,
                                                           onSubmit = () => {},
                                                         }) => {
  const [selectedTenant, setSelectedTenant] = useState<string>('');
  const [selectedPaymentType, setSelectedPaymentType] = useState<string>('rent');
  const [amount, setAmount] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    data: dictionaries,
    isLoading: isDictionariesLoading,
    error: dictionariesError,
  } = useGetDictionaries<PaymentDictionaries>('payments');

  const tenantOptions = React.useMemo(() => {
    if (!dictionaries?.tenants) {
      return [];
    }
    return dictionaries.tenants.map((tenant) => ({
      value: tenant.id.toString(),
      label: tenant.name,
    }));
  }, [dictionaries?.tenants]);

  const paymentTypeOptions = React.useMemo(() => {
    if (!dictionaries?.paymentTypes) {
      return [];
    }
    return dictionaries.paymentTypes.map((type) => ({
      value: type.id,
      label: type.name,
    }));
  }, [dictionaries?.paymentTypes]);

  useEffect(() => {
    if (isOpen) {
      setSelectedTenant('');
      setSelectedPaymentType('rent');
      setAmount('');
      setDescription('');
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const handleTenantChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedTenant(event.target.value);
  };

  const handlePaymentTypeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedPaymentType(event.target.value);
  };

  const handleAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (/^\d*\.?\d*$/.test(value)) {
      setAmount(value);
    }
  };

  const handleDescriptionChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setDescription(event.target.value);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!selectedTenant || !selectedPaymentType || !amount) {
      return;
    }

    setIsSubmitting(true);

    try {
      const paymentData: AddPaymentData = {
        tenantId: selectedTenant,
        paymentType: selectedPaymentType,
        amount: parseFloat(amount),
        description: description.trim(),
      };

      await onSubmit(paymentData);
      onClose();
    } catch (error) {
      console.error('Error submitting payment:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = selectedTenant && selectedPaymentType && amount && !isSubmitting;

  return (
    <UIModal
      isOpen={isOpen}
      onClose={onClose}
      title="Create New Payment"
      size="medium"
      closeOnBackdropClick={true}
      closeOnEscape={true}
    >
      <div className="AddPaymentModal">
        {isDictionariesLoading && (
          <div className="AddPaymentModal__loading">
            <div className="AddPaymentModal__spinner"></div>
            <span>Loading payment options...</span>
          </div>
        )}

        {dictionariesError && (
          <div className="AddPaymentModal__error">
            <span>Failed to load payment options</span>
            <p>{dictionariesError.message}</p>
          </div>
        )}

        {!isDictionariesLoading && !dictionariesError && (
          <form onSubmit={handleSubmit} className="AddPaymentModal__form">
            <div className="AddPaymentModal__header">
              <p className="AddPaymentModal__subtitle">
                Create a new payment request for a tenant. An email with payment link will be sent automatically.
              </p>
            </div>

            <div className="AddPaymentModal__grid">
              <div className="AddPaymentModal__row">
                <div className="AddPaymentModal__field">
                  <label htmlFor="tenant-select" className="AddPaymentModal__label">
                    <span className="AddPaymentModal__required">*</span>
                    <span className="AddPaymentModal__label-text">Tenant</span>
                  </label>
                  <UISelect
                    id="tenant-select"
                    size="medium"
                    value={selectedTenant}
                    onChange={handleTenantChange}
                    options={[
                      { value: '', label: 'Select tenant' },
                      ...tenantOptions,
                    ]}
                    className="AddPaymentModal__select"
                    disabled={isSubmitting}
                  />
                </div>

                <div className="AddPaymentModal__field">
                  <label htmlFor="payment-type-select" className="AddPaymentModal__label">
                    <span className="AddPaymentModal__required">*</span>
                    <span className="AddPaymentModal__label-text">Payment Type</span>
                  </label>
                  <UISelect
                    id="payment-type-select"
                    size="medium"
                    value={selectedPaymentType}
                    onChange={handlePaymentTypeChange}
                    options={paymentTypeOptions}
                    className="AddPaymentModal__select"
                    disabled={isSubmitting}
                  />
                </div>
              </div>

              <div className="AddPaymentModal__field">
                <label htmlFor="amount-input" className="AddPaymentModal__label">
                  <span className="AddPaymentModal__required">*</span>
                  <span className="AddPaymentModal__label-text">Amount</span>
                </label>
                <div className="AddPaymentModal__amount-wrapper">
                  <span className="AddPaymentModal__currency">$</span>
                  <input
                    id="amount-input"
                    type="text"
                    value={amount}
                    onChange={handleAmountChange}
                    placeholder="0.00"
                    className="AddPaymentModal__amount-input"
                    disabled={isSubmitting}
                  />
                </div>
              </div>

              <div className="AddPaymentModal__field">
                <label htmlFor="description-textarea" className="AddPaymentModal__label">
                  <span className="AddPaymentModal__label-text">Description</span>
                  <span className="AddPaymentModal__optional">(Optional)</span>
                </label>
                <textarea
                  id="description-textarea"
                  value={description}
                  onChange={handleDescriptionChange}
                  placeholder="Enter payment description or notes..."
                  className="AddPaymentModal__textarea"
                  rows={3}
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="AddPaymentModal__actions">
              <UIButton
                type="button"
                variant="secondary"
                size="medium"
                outline={true}
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </UIButton>
              <UIButton
                type="submit"
                variant="primary"
                size="medium"
                disabled={!isFormValid}
              >
                {isSubmitting ? 'Creating Payment...' : 'Create Payment'}
              </UIButton>
            </div>
          </form>
        )}
      </div>
    </UIModal>
  );
};

export default AddPaymentModal;